import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { LiveTool } from '../lib/streamStore'

/**
 * Yazilmakta / calismakta olan arac - akan mesajin altinda canli kart.
 *
 * Kullanici istegi: kod yazilirken ve gorsel uretilirken arayuz dakikalarca
 * hareketsiz kaliyordu ve "program kilitlendi" izlenimi veriyordu. Kart:
 *
 *   * kodu / gorsel talimatini model YAZARKEN gosteriyor (`tool_draft`),
 *   * gorselde adim, yuzde ve ara adim onizlemesini gosteriyor (`tool_progress`),
 *   * her durumda gecen sureyi sayiyor - hicbir sey degismese de saniye
 *     ilerliyor, yani "donmus" ile "calisiyor" ayirt edilebiliyor.
 *
 * Gercek arac karti geldiginde bu kart kayboluyor (bkz. `streamStore.setLive`).
 */
export function LiveToolCard({ live }: { live: LiveTool }) {
  const { t } = useTranslation()
  const elapsed = useElapsedSeconds(live.startedAt)
  const isImage = live.tool.endsWith('_image') || live.tool === 'image'
  const isCode = live.tool === 'run_python' || live.tool === 'edit_python'

  return (
    <section className="toolcard live-card" aria-live="polite" aria-busy="true">
      <header>
        <span className="toolcard-kind">
          <span className="live-dot" aria-hidden="true" />
          {status(live, isImage, isCode, t)}
        </span>
        <span className="live-elapsed">{t('tool.live.elapsed', { seconds: elapsed })}</span>
      </header>
      {isImage ? <ImageBody live={live} /> : <DraftText live={live} code={isCode} />}
    </section>
  )
}

function status(
  live: LiveTool,
  isImage: boolean,
  isCode: boolean,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (isImage) {
    if (live.phase === 'loading') return t('tool.live.imageLoading')
    if (live.phase === 'generating') return t('tool.live.imageGenerating')
    if (live.phase === 'decoding') return t('tool.live.imageDecoding')
    if (live.phase === 'checking') return t('tool.live.imageChecking')
    if (live.phase === 'retrying') return t('tool.live.imageRetrying')
    return live.complete ? t('tool.live.imageStarting') : t('tool.live.writingPrompt')
  }
  if (isCode) {
    if (live.complete) return t('tool.live.runningCode')
    return live.tool === 'edit_python' ? t('tool.live.writingFix') : t('tool.live.writingCode')
  }
  return live.complete ? t('tool.live.runningTool') : t('tool.live.preparingTool')
}

function DraftText({ live, code }: { live: LiveTool; code: boolean }) {
  const ref = useRef<HTMLPreElement>(null)
  // Yazilan kodun SONU gorunsun: kutu sabit yukseklikte, her buyumede dibe kaydir.
  useLayoutEffect(() => {
    const element = ref.current
    if (element) element.scrollTop = element.scrollHeight
  }, [live.text])
  if (!live.text) return null
  return (
    <pre ref={ref} className={code ? 'toolcard-code live-code' : 'toolcard-output live-code'}>
      {live.text}
      <span className="live-caret" aria-hidden="true" />
    </pre>
  )
}

function ImageBody({ live }: { live: LiveTool }) {
  const { t } = useTranslation()
  const total = live.total ?? 0
  const step = live.step ?? 0
  const generating = live.phase === 'generating' && total > 0
  // Adimlar %90'a kadar; son hale getirme (tam cozunurlukte cozme, ~15 sn
  // olculdu) %95 - aksi halde cubuk %100'de takili gorunuyordu.
  const percent = generating
    ? Math.round((step / total) * 90)
    : live.phase === 'decoding'
      ? 95
      : null
  const writing = !live.phase && !live.complete

  return (
    <div className="live-image">
      {live.text && (
        <p className={writing ? 'live-prompt live-prompt-writing' : 'live-prompt'}>
          {live.text}
          {writing && <span className="live-caret" aria-hidden="true" />}
        </p>
      )}
      {percent !== null && (
        <div className="live-progress">
          <div
            className="live-progress-bar"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            aria-label={status(live, true, false, t)}
          >
            <span style={{ width: `${percent}%` }} />
          </div>
          <span className="live-progress-label">
            %{percent}
            {generating && <> · {t('tool.live.step', { step, total })}</>}
          </span>
        </div>
      )}
      {live.preview && (
        <img
          className={generating && step < total ? 'live-preview live-preview-draft' : 'live-preview'}
          src={live.preview}
          alt={t('tool.live.previewAlt')}
        />
      )}
    </div>
  )
}

function useElapsedSeconds(startedAt: number): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])
  return Math.max(0, Math.floor((now - startedAt) / 1000))
}
